/**
 * Agent grounding context — the compact, honest "what we know" string the
 * argoWorker seam sends alongside every /interpret call so the real-Claude
 * worker answers and proposes from OUR data instead of vibes.
 *
 * Pure and IO-free by construction: callers hand in store snapshots, this
 * distills them into two plain-text blocks —
 *
 *   COMPANY BRAIN — the top Brain facts by confidence (capped, each tagged
 *   with its provenance + confidence so the model knows what to trust), and
 *
 *   CRM STATS — the same tallies/rollups that power the local backend's
 *   `answerDataQuestion` (shared helpers in ./dataSummary, not duplicated).
 *
 * Payload honesty: the result is hard-capped at {@link MAX_CONTEXT_CHARS};
 * when the cap bites, the string ends with an explicit '…truncated' marker
 * rather than silently dropping data.
 */

import type {
  Account,
  BrainFact,
  Person,
  ProjectTask,
  Ticket,
} from '../db/types';
import { plural, tally, usd } from './dataSummary';

/** Store snapshots the builder distills. All arrays; brain/tasks optional. */
export interface AgentGrounding {
  people: Person[];
  accounts: Account[];
  tickets: Ticket[];
  tasks?: ProjectTask[];
  brainFacts?: BrainFact[];
}

/** Hard cap on the grounding payload (chars), guarding the prompt budget. */
export const MAX_CONTEXT_CHARS = 6000;

/** Honest marker appended when the cap truncates the context. */
export const TRUNCATION_MARKER = ' …truncated';

/** How many Brain facts (by confidence, desc) make the cut. */
export const MAX_BRAIN_FACTS = 15;

/** `[source 72%]` provenance tag for one fact. */
function provenanceTag(f: BrainFact): string {
  return `[${f.source} ${Math.round(f.confidence * 100)}%]`;
}

/**
 * Build the grounding string. Deterministic, pure, and always within
 * {@link MAX_CONTEXT_CHARS} (with the honest marker when truncated).
 */
export function buildAgentContext(data: AgentGrounding): string {
  const lines: string[] = [];

  // --- Company Brain: top facts by confidence, provenance-tagged ---------
  const accountName = (id: string): string =>
    data.accounts.find((a) => a.id === id)?.name ?? id;
  const topFacts = [...(data.brainFacts ?? [])]
    .sort((a, b) => b.confidence - a.confidence)
    .slice(0, MAX_BRAIN_FACTS);
  lines.push('COMPANY BRAIN (top facts by confidence):');
  if (topFacts.length === 0) {
    lines.push('- no facts recorded yet');
  } else {
    for (const f of topFacts) {
      lines.push(
        `- ${provenanceTag(f)} ${accountName(f.accountId)} · ${f.section} — ${f.label}: ${f.value}`,
      );
    }
  }

  // --- CRM stats: the same rollups answerDataQuestion serves -------------
  const prospects = data.people.filter((p) => p.category === 'prospect');
  const customers = data.people.filter((p) => p.category === 'customer');
  const openProspects = prospects.filter(
    (p) => p.stage !== 'closed-won' && p.stage !== 'closed-lost',
  );
  const activeCustomers = customers.filter((c) => c.status !== 'churned');
  const pipeline = openProspects.reduce((s, p) => s + (p.estimatedValue ?? 0), 0);
  const arr = activeCustomers.reduce((s, c) => s + (c.accountValue ?? 0), 0);
  const openTickets = data.tickets.filter(
    (t) => t.status !== 'done' && t.status !== 'cancelled',
  );
  const tasks = data.tasks ?? [];
  const openTasks = tasks.filter((t) => t.status !== 'done');

  const stageTally = tally(prospects, (p) => p.stage);
  const statusTally = tally(customers, (p) => p.status);
  lines.push('');
  lines.push('CRM STATS:');
  lines.push(`- ${plural(prospects.length, 'prospect')}${stageTally ? ` (${stageTally})` : ''}`);
  lines.push(`- ${plural(customers.length, 'customer')}${statusTally ? ` (${statusTally})` : ''}`);
  lines.push(
    `- open pipeline ${usd(pipeline)} across ${plural(openProspects.length, 'deal')} · active ARR ${usd(arr)} across ${plural(activeCustomers.length, 'customer')}`,
  );
  lines.push(`- ${plural(data.accounts.length, 'account')}`);
  lines.push(
    `- ${plural(data.tickets.length, 'ticket')} (${openTickets.length} open) · ${plural(tasks.length, 'task')} (${openTasks.length} open)`,
  );

  const out = lines.join('\n');
  if (out.length <= MAX_CONTEXT_CHARS) return out;
  return out.slice(0, MAX_CONTEXT_CHARS - TRUNCATION_MARKER.length) + TRUNCATION_MARKER;
}
